class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Capture backdrop interactions even after cart section replaces the overlay.
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay') || (event.target === this && this.classList.contains('active'))) {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      }
    }, true);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.classList.remove('is-closing');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    // Two separate render frames ensure a genuine off-screen start on mobile.
    cancelAnimationFrame(this._openFrame);
    this._openFrame = requestAnimationFrame(() => {
      this._openFrame = requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });
    });

    const onDrawerOpenEnd = (event) => {
        if (event.target !== this.querySelector('.drawer__inner') || event.propertyName !== 'transform') return;
        this.removeEventListener('transitionend', onDrawerOpenEnd);
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', onDrawerOpenEnd);

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    cancelAnimationFrame(this._openFrame);
    if (this.classList.contains('is-closing') || !this.classList.contains('active')) return;
    this.classList.add('is-closing');
    this.classList.remove('active');
    const inner = this.querySelector('.drawer__inner');
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.classList.remove('is-closing');
      removeTrapFocus(this.activeElement || null);
      document.body.classList.remove('overflow-hidden');
      this.dispatchEvent(new CustomEvent('drawer:closed', { bubbles: true }));
    };
    inner?.addEventListener('transitionend', e => { if (e.target === inner && e.propertyName === 'transform') finish(); }, { once: true });
    setTimeout(finish, 500);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    // Keep the outer cart-drawer state in sync with newly rendered Shopify HTML.
    // The reference CSS hides cart-drawer-items whenever this element has
    // "is-empty"; leaving the initial empty state here hides new products.
    const drawerSectionHTML = parsedState.sections?.['cart-drawer'];
    if (drawerSectionHTML) {
      const incoming = new DOMParser().parseFromString(drawerSectionHTML, 'text/html');
      const incomingDrawer = incoming.querySelector('cart-drawer');
      if (incomingDrawer) {
        this.classList.toggle('is-empty', incomingDrawer.classList.contains('is-empty'));
      } else if (typeof parsedState.item_count === 'number') {
        this.classList.toggle('is-empty', parsedState.item_count === 0);
      }
    } else if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHTML) return;
      const replacement = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector(section.selector || '.shopify-section');
      if (replacement) sectionElement.innerHTML = replacement.innerHTML;
    });

    // The outer drawer element remains mounted while its inner section changes.
    const currentInner = this.querySelector('.drawer__inner');
    if (currentInner) currentInner.classList.remove('is-empty');

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* WEAVEPLACE v1.2 — normalize all successful add-to-cart entry points. */
(function () {
  if (window.__wpCartDrawerSyncInstalled) return;
  window.__wpCartDrawerSyncInstalled = true;

  let refreshSerial = 0;
  let refreshTimer = null;
  let ongoingRefresh = null;
  let lastAddedAt = 0;

  function drawer() { return document.querySelector('cart-drawer'); }
  function sectionUrl() {
    const url = new URL(window.routes?.cart_url || '/cart', location.origin);
    url.searchParams.set('section_id', 'cart-drawer');
    return url.toString();
  }
  function headerOffset() {
    // The position is measured at opening time, never inferred from an
    // announcement-bar height on another template.
    if (window.matchMedia('(max-width: 767px)').matches) return 0;
    const header = document.querySelector('.section-header');
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;
    return Math.max(0, Math.min(window.innerHeight - 1, Math.round(rect.bottom)));
  }
  function syncOffset(el) {
    el?.style.setProperty('--wp-cart-header-offset', `${headerOffset()}px`);
  }
  // Called on every open, including Header icon clicks and post-add auto-open.
  const nativeOpen = CartDrawer.prototype.open;
  CartDrawer.prototype.open = function (...args) {
    syncOffset(this);
    return nativeOpen.apply(this, args);
  };
  async function refresh(openAfter = false) {
    const el = drawer();
    if (!el) return;
    const serial = ++refreshSerial;
    syncOffset(el);
    try {
      // Shopify section rendering provides the complete cart and its correct empty state.
      const response = await fetch(sectionUrl(), { credentials: 'same-origin', cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error(`Cart section HTTP ${response.status}`);
      const markup = await response.text();
      if (serial !== refreshSerial) return;
      const source = new DOMParser().parseFromString(markup, 'text/html').querySelector('cart-drawer');
      const incoming = source?.querySelector('#CartDrawer');
      const target = el.querySelector('#CartDrawer');
      if (!source || !incoming || !target) throw new Error('Cart drawer section missing');
      const wasOpen = el.classList.contains('active');
      // Avoid re-creating the entire drawer at the end of its opening transition.
      // Shopify HTML can contain changing view-event metadata, so compare only
      // the visible cart data rather than the whole HTML string.
      const cartSignature = (node) => {
        const rows = Array.from(node.querySelectorAll('[id^="CartDrawer-Item-"]'));
        return JSON.stringify({
          empty: !!node.querySelector('.drawer__inner-empty'),
          rows: rows.map(row => [
            row.id,
            row.querySelector('.cart-item__name')?.textContent?.trim() || '',
            row.querySelector('.quantity__input')?.value || '',
            row.querySelector('.cart-item__details .product-option')?.textContent?.trim() || ''
          ]),
          count: node.querySelector('.cart-item__count, .wp-cart-count')?.textContent?.trim() || '',
          total: node.querySelector('#CartDrawer-Checkout strong')?.textContent?.trim() || ''
        });
      };
      const sameContent = cartSignature(target) === cartSignature(incoming);
      if (!sameContent) {
        // Keep the currently animating .drawer__inner element mounted. Replacing
        // that element is what causes a second paint / animation jump on mobile.
        const currentInner = target.querySelector('.drawer__inner');
        const nextInner = incoming.querySelector('.drawer__inner');
        if (currentInner && nextInner) {
          currentInner.innerHTML = nextInner.innerHTML;
        } else {
          target.innerHTML = incoming.innerHTML;
        }
      }
      el.classList.toggle('is-empty', source.classList.contains('is-empty'));
      syncOffset(el);
      // The parent drawer already handles overlay clicks via delegated listener.
      if (openAfter && !wasOpen) el.open(document.querySelector('#cart-icon-bubble') || undefined);
      else if (wasOpen) el.classList.add('active');
    } catch (error) {
      console.warn('[WEAVEPLACE Cart] Could not refresh drawer:', error);
      if (openAfter && !el.classList.contains('active')) el.open(document.querySelector('#cart-icon-bubble') || undefined);
    }
  }
  function queueRefresh(openAfter = true) {
    lastAddedAt = Date.now();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { ongoingRefresh = refresh(openAfter); }, 135);
  }

  // Native Dawn product form may call renderContents itself. Refresh after that
  // and also support custom PLP/product-card AJAX flows that don't call it.
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const requestUrl = typeof input === 'string' ? input : input?.url || '';
    const isAdd = /\/cart\/add(?:\.js)?(?:\?|$)/.test(requestUrl);
    const result = nativeFetch.apply(this, arguments);
    if (isAdd) {
      result.then(response => {
        if (response.ok) {
          const silent = init?.headers && (init.headers['X-WP-PDP-Mobile'] === '1' || (typeof Headers !== 'undefined' && new Headers(init.headers).get('X-WP-PDP-Mobile') === '1'));
          queueRefresh(!silent);
        }
      }).catch(() => {});
    }
    return result;
  };

  document.addEventListener('cart:updated', event => {
    if (event?.detail?.item && Date.now() - lastAddedAt > 900) queueRefresh(!event.detail.silent);
  });

  // On click, fetch fresh section even if a product was added by an app,
  // without repeatedly opening an already visible drawer.
  document.addEventListener('click', event => {
    if (!event.target.closest('#cart-icon-bubble')) return;
    if (Date.now() - lastAddedAt < 650) return;
    setTimeout(() => refresh(false), 60);
  }, true);

  window.addEventListener('resize', () => syncOffset(drawer()), { passive: true });
  window.addEventListener('scroll', () => { if (drawer()?.classList.contains('active')) syncOffset(drawer()); }, { passive: true });
  window.__wpRefreshCartDrawer = refresh;
})();
